/**
 * terrain - Elevation-Sampler + Heightfield-Sampling für die 3D-Szene (#602)
 *
 * Reine, WebGL-freie Logik (keine React-/three-/fabric-Imports). Implementiert
 * die verbindliche Höhensemantik aus PLAN_TERRAIN_GEFAELLE_623.md §2
 * (Modell-Revision 2026-07-15):
 *
 *   elevation(p) = zoneLevel(p) + pondOffset(p)
 *
 * - base(p):      Projektion auf die Gefällerichtung, linear 0 (höchster
 *                 Rand der Outline-Bounds) → −dropCm (tiefster Rand)
 * - Ebenen:       kind 'plateau', waagerecht (base(Zentroid) + height);
 *                 kleinste enthaltende Ebene gewinnt (Verschachtelung/Senken)
 * - Böschungen:   kind 'slope', OHNE eigene Höhe — Höhe per IDW aus den
 *                 angrenzenden Ebenen interpoliert (an der gemeinsamen Kante
 *                 stetig auf Ebenen-Niveau, dazwischen sanft). Breite = die
 *                 gezeichnete Ausdehnung. Berührt keine Ebene ⇒ Grundniveau
 * - offener Boden: außerhalb aller Zonen gilt base(p) (harte Kante zu Ebenen)
 * - Teich-Mulde:  senkt das Gelände relativ zum lokalen Niveau ab (#602,
 *                 konkretisiert aus dem #600-Befund) — unabhängig von
 *                 heightDifferences.enabled
 *
 * Einheiten: Meter, Koordinaten Welt-(x, z) wie im SceneModel.
 */

import { pointToWorld, cmToM } from './units';
import { polygonArea } from './shapeSampling';

// --- Konstanten (Tests referenzieren sie) -----------------------------------

/** Eine Böschung gilt als an eine Ebene angrenzend, wenn ihre Ränder näher
 *  als das beieinander liegen (Adjazenz für die Interpolation, #625). */
export const ZONE_NEIGHBOR_TOLERANCE_M = 0.1;
/** Blend-Distanz, über die offener Boden ins Grundniveau übergeht, wenn er das
 *  Niveau einer TIEFEREN Nachbarzone erbt (#629-Follow-up gegen schwebende Fins). */
export const ZONE_OPEN_BLEND_M = 0.5;
/** Muldentiefe unter Teichen (relativ zum lokalen Geländeniveau).
 *  0,25 → 0,45 (#694): bei 0,25 lagen zwischen Wasserspiegel und Muldengrund
 *  nur 10 cm — durch das transparente Wasser schien der Grund flächig durch,
 *  und wo man den Grund sieht, liest das Auge „flach". Gartenteiche liegen
 *  real bei 60–120 cm. */
export const POND_DEPTH_M = 0.45;
/** OBERGRENZE der Smoothstep-Randbreite der Teich-Mulde. Die tatsächliche
 *  Breite bemisst pondRimFor() pro Teich größenrelativ — ein absoluter Rand
 *  (0,5 → 0,35 nach #602-Checkpoint) belegte bei einem 1,2-m-Teich fast den
 *  ganzen Radius und machte ihn zur Schüssel (#694). */
export const POND_RIM_M = 0.35;
/** Anteil des Teich-Innenradius, den der Böschungsübergang höchstens einnimmt.
 *  Ab ~2 m Innendurchmesser greift stattdessen der Deckel POND_RIM_M, damit
 *  große Teiche ein natürlich wirkendes Ufer behalten. */
export const POND_RIM_INRADIUS_FRACTION = 0.35;
/** Untergrenze der Randbreite — hält d/rim bei entarteten Polygonen endlich. */
export const POND_RIM_MIN_M = 0.05;
/** Wasserfläche liegt so weit unter dem lokalen Umgebungsniveau.
 *  0,15 → 0,05 (#694): ein angelegter Teich steht nahezu randvoll; ein
 *  deutlich abgesenkter Spiegel wirkt wie ein leergelaufenes Becken. */
export const WATER_BELOW_TERRAIN_M = 0.05;
/** Ziel-Rasterauflösung des Heightfields (Meter pro Sample).
 *  0,5 → 0,25 nach #602-Checkpoint (löst Teich-Mulden und Zonenkanten auf),
 *  0,25 → 0,125 nach #694-Sichttest: das Gelände facettierte sichtbar, ein
 *  1-m-Teich lag auf ~4 Rasterpunkten. Bezahlbar wurde das erst durch den
 *  Box-Vorfilter in den Sampler-Schleifen (siehe polygonBounds). */
export const TERRAIN_RESOLUTION_M = 0.125;
/** Harter Deckel der Heightfield-Vertices pro Achse. Mit der Auflösung
 *  mitgezogen (128 → 256), damit der Deckel bei derselben Gartengröße greift
 *  wie bisher (128 · 0,25 m = 256 · 0,125 m = 32 m Kantenlänge). */
export const TERRAIN_MAX_VERTICES_PER_AXIS = 256;
/** Despike (#629): Eine innere offener-Boden-Spitze wird nur geglättet, wenn sie
 *  mehr als das über ihre höchste innere Nachbarhöhe ragt. */
export const TERRAIN_DESPIKE_MIN_DROP_M = 0.05;
/** Wiederholungen des Despike-Passes (glättet auch 1-Sample-Grate zuverlässig). */
const TERRAIN_DESPIKE_PASSES = 2;

const EPS = 1e-9;

// --- Geometrie-Helfer (Welt-Koordinaten {x, z}) ------------------------------

/** Hermite-Smoothstep über t ∈ [0, 1]. */
export function smoothstep(t) {
  const c = Math.min(1, Math.max(0, t));
  return c * c * (3 - 2 * c);
}

/** Punkt-in-Polygon (Ray-Casting, Kante zählt als innen-nah genug). */
export function pointInPolygon(p, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    const intersects =
      a.z > p.z !== b.z > p.z &&
      p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Abstand Punkt → Strecke a–b. */
export function distanceToSegment(p, a, b) {
  const abx = b.x - a.x;
  const abz = b.z - a.z;
  const lenSq = abx * abx + abz * abz;
  let t = 0;
  if (lenSq > EPS) {
    t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / lenSq;
    t = Math.min(1, Math.max(0, t));
  }
  const dx = p.x - (a.x + t * abx);
  const dz = p.z - (a.z + t * abz);
  return Math.hypot(dx, dz);
}

/** Kürzester Abstand zum Polygonrand (alle Kanten). */
function distanceToPolygonBoundary(p, polygon) {
  let min = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const d = distanceToSegment(p, polygon[j], polygon[i]);
    if (d < min) min = d;
  }
  return min;
}

/**
 * Punkt-in-Polygon inklusive Rand (1-mm-Toleranz): Ray-Casting ist für
 * Punkte EXAKT auf einer Kante ambivalent — Grid-Samples auf der Outline
 * und gesnappte Rand-Vertices müssen aber deterministisch zur Zone gehören
 * (Fall „Zone bis zum Zaun", sonst Höhen-Dips am Rand).
 */
const BOUNDARY_EPS_M = 0.001;

function insideInclusive(p, polygon) {
  if (pointInPolygon(p, polygon)) return true;
  return distanceToPolygonBoundary(p, polygon) <= BOUNDARY_EPS_M;
}

// --- Bounding-Boxen (#694): Vorfilter für die Sampler-Schleifen ---------------
//
// elevationAt läuft einmal pro Heightfield-Rasterpunkt und prüfte jeden Punkt
// gegen JEDES Polygon — bei bezier-gesampelten Teichen (~50 Punkte) sind das pro
// Sample hunderte Segment-Abstände, obwohl die allermeisten Samples weit weg
// liegen. Die Box entscheidet das mit vier Vergleichen vor. Ergebnisse bleiben
// exakt: verworfen wird nur, was die genaue Prüfung ohnehin verworfen hätte.

/** Achsenparallele Bounding-Box eines Polygons. */
function polygonBounds(polygon) {
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const v of polygon) {
    if (v.x < minX) minX = v.x;
    if (v.x > maxX) maxX = v.x;
    if (v.z < minZ) minZ = v.z;
    if (v.z > maxZ) maxZ = v.z;
  }
  return { minX, maxX, minZ, maxZ };
}

/** Sicher außerhalb der Box (inkl. Rand-Toleranz) ⇒ nie insideInclusive. */
function outsideBounds(p, b) {
  return (
    p.x < b.minX - BOUNDARY_EPS_M ||
    p.x > b.maxX + BOUNDARY_EPS_M ||
    p.z < b.minZ - BOUNDARY_EPS_M ||
    p.z > b.maxZ + BOUNDARY_EPS_M
  );
}

/** insideInclusive mit Box-Vorfilter (Eintrag trägt polygon + bounds). */
function coversPoint(p, entry) {
  return !outsideBounds(p, entry.bounds) && insideInclusive(p, entry.polygon);
}

/** Untere Schranke für den Abstand p → Polygonrand (Abstand zur Box). */
function distanceToBounds(p, b) {
  const dx = Math.max(b.minX - p.x, 0, p.x - b.maxX);
  const dz = Math.max(b.minZ - p.z, 0, p.z - b.maxZ);
  return Math.hypot(dx, dz);
}

/** Nächster Punkt auf dem Polygonrand (für Boundary-Snap des Heightfields). */
export function closestPointOnPolygon(p, polygon) {
  let best = null;
  let bestD = Infinity;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j];
    const b = polygon[i];
    const abx = b.x - a.x;
    const abz = b.z - a.z;
    const lenSq = abx * abx + abz * abz;
    let t = 0;
    if (lenSq > EPS) {
      t = ((p.x - a.x) * abx + (p.z - a.z) * abz) / lenSq;
      t = Math.min(1, Math.max(0, t));
    }
    const q = { x: a.x + t * abx, z: a.z + t * abz };
    const d = Math.hypot(p.x - q.x, p.z - q.z);
    if (d < bestD) {
      bestD = d;
      best = q;
    }
  }
  return best;
}

/** Flächen-gewichteter Zentroid (Shoelace); Fallback: Vertex-Mittel. */
export function polygonCentroid(polygon) {
  let areaTwice = 0;
  let cx = 0;
  let cz = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[j];
    const b = polygon[i];
    const cross = a.x * b.z - b.x * a.z;
    areaTwice += cross;
    cx += (a.x + b.x) * cross;
    cz += (a.z + b.z) * cross;
  }
  if (Math.abs(areaTwice) < EPS) {
    const n = polygon.length || 1;
    return {
      x: polygon.reduce((s, v) => s + v.x, 0) / n,
      z: polygon.reduce((s, v) => s + v.z, 0) / n,
    };
  }
  return { x: cx / (3 * areaTwice), z: cz / (3 * areaTwice) };
}

// --- Basisebene (baseSlope) --------------------------------------------------

/**
 * Basisebenen-Funktion aus baseSlope + Outline-Bounds-Projektion.
 * directionDeg ist ein Canvas-Winkel (0 = oben, 90 = rechts, im Uhrzeigersinn)
 * und bezeichnet die Richtung, in die das Gelände ABFÄLLT.
 */
function createBaseFn(baseSlope, outlineWorld) {
  const dropM = cmToM(baseSlope?.dropCm ?? 0);
  if (!baseSlope || !(dropM > 0) || outlineWorld.length === 0) {
    return () => 0;
  }

  const rad = ((baseSlope.directionDeg ?? 0) * Math.PI) / 180;
  // Canvas-Winkel → Fallrichtung in Welt-(x, z): 0° = oben = −z
  const dir = { x: Math.sin(rad), z: -Math.cos(rad) };

  let tMin = Infinity;
  let tMax = -Infinity;
  for (const v of outlineWorld) {
    const t = v.x * dir.x + v.z * dir.z;
    if (t < tMin) tMin = t;
    if (t > tMax) tMax = t;
  }
  const span = tMax - tMin;
  if (!(span > EPS)) return () => 0; // degenerierte Bounds absichern

  return (p) => {
    const t = p.x * dir.x + p.z * dir.z;
    const norm = Math.min(1, Math.max(0, (t - tMin) / span)); // Clamping außerhalb
    return -dropM * norm;
  };
}

// --- Zonen: Ebenen (Plateaus) + Böschungen -----------------------------------
//
// Modell (Revision 2026-07-15, Plan §2): Der User zeichnet ebene Flächen
// (kind 'plateau', mit Höhe) und Böschungen (kind 'slope', OHNE Höhe). Eine
// Böschung interpoliert ihre Höhe aus den Ebenen, an deren Rand sie liegt:
//   level(p) = Σ Lᵢ/dᵢ ÷ Σ 1/dᵢ   (IDW über angrenzende Ebenen, dᵢ = Abstand
//   zum Ebenen-Rand). An der gemeinsamen Kante (dᵢ→0) läuft sie stetig auf
//   Ebenen-Niveau, dazwischen sanft. Breite = die gezeichnete Ausdehnung.
// Ebenen auf offenem Boden = harte Kante (Grundniveau außerhalb aller Zonen).

/** Sample-Dichte, mit der eine Böschungskante gegen Ebenen-Ränder geprüft wird. */
const ADJACENCY_SAMPLES = 7;
/** Untergrenze der IDW-Distanz, damit 1/d nicht explodiert. */
const IDW_MIN_DIST_M = 1e-4;

/** Kürzester Abstand zwischen zwei Polygon-Rändern (Sampling entlang A). */
function boundariesTouch(polyA, polyB, tol) {
  for (let i = 0, j = polyA.length - 1; i < polyA.length; j = i++) {
    const a = polyA[j];
    const b = polyA[i];
    for (let s = 0; s < ADJACENCY_SAMPLES; s++) {
      const t = s / (ADJACENCY_SAMPLES - 1);
      const q = { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
      if (distanceToPolygonBoundary(q, polyB) <= tol) return true;
    }
  }
  return false;
}

/**
 * Zonen aufteilen in Ebenen (plateaus, mit absoluter Höhe, groß→klein sortiert)
 * und Böschungen (slopes, mit Liste der angrenzenden Ebenen für die IDW).
 * Rückwärtskompatibel: eine Zone ohne `kind` gilt als Ebene (Bestand hatte
 * nur Höhenzonen).
 */
function prepareZones(heightDifferences, pixelsPerCm, baseFn) {
  if (!heightDifferences?.enabled || !Array.isArray(heightDifferences.zones)) {
    return { plateaus: [], slopes: [] };
  }

  const plateaus = [];
  const slopePolys = [];
  for (const zone of heightDifferences.zones) {
    if (!Array.isArray(zone?.vertices) || zone.vertices.length < 3) continue;
    const polygon = zone.vertices.map((v) => pointToWorld(v, pixelsPerCm));
    if (zone.kind === 'slope') {
      slopePolys.push(polygon);
    } else {
      plateaus.push({
        polygon,
        bounds: polygonBounds(polygon),
        areaM2: Math.abs(polygonArea(polygon)),
        // Ebenen sind waagerecht: absolute Höhe am Zentroid festgemacht (§2)
        level: baseFn(polygonCentroid(polygon)) + cmToM(zone.height ?? 0),
      });
    }
  }

  // Überlappungsregel: kleinere Ebene übersteuert größere ⇒ groß→klein, damit
  // beim Containment-Check die zuletzt getroffene (kleinste) gewinnt.
  plateaus.sort((a, b) => b.areaM2 - a.areaM2);

  // Böschung: an welche Ebenen grenzt sie? (Ränder berühren sich ~10 cm)
  const slopes = slopePolys.map((polygon) => ({
    polygon,
    bounds: polygonBounds(polygon),
    adjacent: plateaus.filter((pl) =>
      boundariesTouch(polygon, pl.polygon, ZONE_NEIGHBOR_TOLERANCE_M)
    ),
  }));

  return { plateaus, slopes };
}

/** Höhe eines Punkts in einer Böschung: IDW über die angrenzenden Ebenen. */
function slopeLevelAt(p, slope, baseFn) {
  if (slope.adjacent.length === 0) return baseFn(p); // keine Ebene ⇒ Grundniveau
  let wSum = 0;
  let wLevel = 0;
  for (const pl of slope.adjacent) {
    const d = Math.max(distanceToPolygonBoundary(p, pl.polygon), IDW_MIN_DIST_M);
    const w = 1 / d;
    wSum += w;
    wLevel += w * pl.level;
  }
  return wLevel / wSum;
}

// --- Teich-Mulden ------------------------------------------------------------

/** Umfang eines Polygons (Welt-Meter). */
function polygonPerimeter(polygon) {
  let sum = 0;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    sum += Math.hypot(polygon[i].x - polygon[j].x, polygon[i].z - polygon[j].z);
  }
  return sum;
}

/**
 * Größenrelative Randbreite der Teich-Mulde (#694).
 *
 * Der Böschungs-Übergang war absolut (POND_RIM_M) und damit bei kleinen Teichen
 * unverhältnismäßig breit: Ein 6-m-Teich verkraftet 0,35 m mühelos, bei 1,2 m
 * Teichbreite frisst der Rand fast den ganzen Radius — genau dort entstand die
 * Schüsselform. Relativ bemessen schrumpft die sichtbare Wasserfläche bei allen
 * Größen um denselben (kleinen) Anteil.
 *
 * Innenradius-Näherung 2·A/U: für Kreis UND Quadrat exakt der Inkreisradius und
 * für gezeichnete Teichformen nah genug — eine echte Inkreis-Bestimmung wäre pro
 * Teich deutlich teurer, ohne dass die 0,25-m-Rasterauflösung davon profitierte.
 */
export function pondRimFor(polygon) {
  const perimeter = polygonPerimeter(polygon);
  if (!(perimeter > EPS)) return POND_RIM_M;
  const inradius = (2 * Math.abs(polygonArea(polygon))) / perimeter;
  return Math.max(
    POND_RIM_MIN_M,
    Math.min(POND_RIM_M, POND_RIM_INRADIUS_FRACTION * inradius)
  );
}

// Ponds werden entweder als reines Polygon ({x,z}[]) oder als { polygon, depthM }
// übergeben (#700 teich.depth). Ohne depthM greift die Default-Muldentiefe
// POND_DEPTH_M — so bleiben die reinen Terrain-Unit-Tests (bare-Polygon) gültig.
function preparePonds(ponds) {
  return (ponds || [])
    .map((entry) => (Array.isArray(entry) ? { polygon: entry } : entry))
    .filter((entry) => Array.isArray(entry?.polygon) && entry.polygon.length >= 3)
    .map(({ polygon, depthM }) => ({
      polygon,
      depthM: depthM > 0 ? depthM : POND_DEPTH_M,
      bounds: polygonBounds(polygon),
      rimM: pondRimFor(polygon),
    }));
}

function pondOffsetAt(p, preparedPonds) {
  let offset = 0;
  for (const pond of preparedPonds) {
    if (!coversPoint(p, pond)) continue;
    const d = distanceToPolygonBoundary(p, pond.polygon);
    const o = -pond.depthM * smoothstep(d / pond.rimM);
    if (o < offset) offset = o; // tiefste Mulde gewinnt (Überlappung)
  }
  return offset;
}

// --- Sampler -----------------------------------------------------------------

/**
 * Elevation-Sampler aus Settings + Teich-Polygonen bauen.
 *
 * @param {object} params
 * @param {object|null} params.heightDifferences - settings.heightDifferences
 *   (Zonen-vertices in Canvas-px; baseSlope optional; Zone hat kind 'plateau'|'slope')
 * @param {object|null} params.outline - settings.outline ({vertices} in px)
 * @param {Array<Array<{x,z}>>|Array<{polygon,depthM}>} [params.ponds] - Teiche
 *   in Welt-Metern: reines Polygon (Default-Muldentiefe) oder { polygon, depthM }
 *   mit eigener Muldentiefe in Metern (#700 teich.depth)
 * @param {number} [params.pixelsPerCm=1]
 * @returns {{ elevationAt: Function, elevationWithoutPonds: Function, isActive: boolean }}
 */
export function createElevationSampler({
  heightDifferences,
  outline,
  ponds,
  pixelsPerCm = 1,
} = {}) {
  const outlineWorld = (outline?.vertices || []).map((v) => pointToWorld(v, pixelsPerCm));

  const baseSlope = heightDifferences?.baseSlope ?? null;
  const baseFn = createBaseFn(baseSlope, outlineWorld);
  const { plateaus, slopes } = prepareZones(heightDifferences, pixelsPerCm, baseFn);
  const preparedPonds = preparePonds(ponds);

  const hasSlope = baseSlope != null && cmToM(baseSlope.dropCm ?? 0) > 0 && outlineWorld.length >= 3;
  const isActive =
    hasSlope || plateaus.length > 0 || slopes.length > 0 || preparedPonds.length > 0;

  const hasZones = plateaus.length > 0 || slopes.length > 0;

  // Niveau + Randabstand der zu p nächstgelegenen Zone (für den Blend des offenen
  // Bodens, #629-Follow-up). Böschungen liefern ihr IDW-Niveau an p.
  // Box-Vorfilter: distanceToBounds ist eine untere Schranke des Randabstands —
  // liegt schon die Box weiter weg als der bisher beste Treffer, kann das Polygon
  // ihn nicht unterbieten und die Segment-Schleife entfällt.
  const nearestZoneLevel = (p) => {
    let best = Infinity;
    let level = null;
    for (const pl of plateaus) {
      if (distanceToBounds(p, pl.bounds) >= best) continue;
      const d = distanceToPolygonBoundary(p, pl.polygon);
      if (d < best) {
        best = d;
        level = pl.level;
      }
    }
    for (const sl of slopes) {
      if (distanceToBounds(p, sl.bounds) >= best) continue;
      const d = distanceToPolygonBoundary(p, sl.polygon);
      if (d < best) {
        best = d;
        level = slopeLevelAt(p, sl, baseFn);
      }
    }
    return { dist: best, level };
  };

  const elevationWithoutPonds = (p) => {
    // 1) In einer Ebene? Kleinste enthaltende gewinnt (plateaus sind groß→klein
    //    sortiert ⇒ die zuletzt getroffene ist die kleinste). Waagerechtes Plateau.
    let plateauLevel = null;
    for (const pl of plateaus) {
      if (coversPoint(p, pl)) plateauLevel = pl.level;
    }
    if (plateauLevel !== null) return plateauLevel;

    // 2) In einer Böschung? Höhe aus den angrenzenden Ebenen interpolieren.
    for (const slope of slopes) {
      if (coversPoint(p, slope)) return slopeLevelAt(p, slope, baseFn);
    }

    // 3) Offener Boden = Grundniveau — ABER erbt das Niveau der nächsten Zone,
    //    wenn diese TIEFER liegt, sanft über ZONE_OPEN_BLEND_M ins Grundniveau
    //    übergehend (#629-Follow-up). Ohne diese Regel schweben unbedeckte
    //    Sub-grid-Streifen (Zeichen-Ungenauigkeit an Zonen-/Outline-Nähten) auf
    //    dem Grundniveau (0 ohne Gefälle) über tiefen Plateaus als Fins/Nadeln —
    //    in 3D UND 2D-Schatten. Nur nach UNTEN: erhöhte Zonen (Hochbeete) behalten
    //    ihre scharfe Kante.
    const base = baseFn(p);
    if (!hasZones) return base;
    const nz = nearestZoneLevel(p);
    if (nz.level === null || nz.level >= base) return base;
    const t = smoothstep(nz.dist / ZONE_OPEN_BLEND_M);
    return nz.level * (1 - t) + base * t;
  };

  const elevationAt = (p) => elevationWithoutPonds(p) + pondOffsetAt(p, preparedPonds);

  // Deckt eine Ebene/Böschung diesen Punkt? (offener Boden = false). Nur für den
  // Heightfield-Despike (#629): geglättet werden ausschließlich offener-Boden-Spitzen.
  const zoneCoverAt = (p) => {
    for (const pl of plateaus) if (coversPoint(p, pl)) return true;
    for (const slope of slopes) if (coversPoint(p, slope)) return true;
    return false;
  };

  // Konservative Untergrenze aller erreichbaren Niveaus (#631): Basisgefälle
  // fällt maximal auf −dropM, Ebenen auf ihr Level, Teiche senken zusätzlich um
  // ihre (tiefste) Muldentiefe. Etwas zu tief ist unschädlich (Marsch-Fenster
  // minimal länger).
  const dropM = cmToM(baseSlope?.dropCm ?? 0);
  let minLevel = Math.min(0, -(dropM > 0 ? dropM : 0));
  for (const pl of plateaus) minLevel = Math.min(minLevel, pl.level);
  if (preparedPonds.length > 0) {
    minLevel -= Math.max(...preparedPonds.map((pond) => pond.depthM));
  }

  return {
    elevationAt,
    elevationWithoutPonds,
    zoneCoverAt,
    isActive,
    minLevel,
    zoneCount: plateaus.length + slopes.length,
  };
}

/**
 * Terrain-Sampler für den shadowCaster (#631) aus der serialisierbaren
 * SceneModel-Spezifikation (`meta.terrainSpec`) rekonstruieren — funktioniert
 * damit auch im Heatmap-Worker, über dessen postMessage nur Plain-Data reist.
 *
 * @param {object|null} terrainSpec - { heightDifferences, outline, ponds, pixelsPerCm }
 * @returns {{elevationAt: Function, minLevel: number}|null} null = flacher Garten
 */
export function createShadowTerrain(terrainSpec) {
  if (!terrainSpec) return null;
  const sampler = createElevationSampler(terrainSpec);
  if (!sampler.isActive) return null;
  return { elevationAt: sampler.elevationAt, minLevel: sampler.minLevel };
}

// --- Heightfield-Sampling ----------------------------------------------------

/**
 * Grundfläche als Raster sampeln (~0,5 m/Sample, Deckel 128×128 Vertices).
 * Row-major: heights[iz * nx + ix] an (minX + ix·stepX, minZ + iz·stepZ).
 *
 * @param {Function} elevationAt - Sampler aus createElevationSampler
 * @param {object} boundsM - { minX, maxX, minZ, maxZ, width, depth } (Meter)
 * @param {object} [options]
 * @returns {{ heights: Float32Array, nx: number, nz: number,
 *             stepX: number, stepZ: number, boundsM: object }}
 */
export function buildHeightfieldSamples(
  elevationAt,
  boundsM,
  {
    targetResolutionM = TERRAIN_RESOLUTION_M,
    maxVerticesPerAxis = TERRAIN_MAX_VERTICES_PER_AXIS,
  } = {}
) {
  const axisCount = (span) =>
    Math.max(2, Math.min(maxVerticesPerAxis, Math.floor(span / targetResolutionM) + 2));

  const nx = axisCount(boundsM.width);
  const nz = axisCount(boundsM.depth);
  const stepX = boundsM.width / (nx - 1);
  const stepZ = boundsM.depth / (nz - 1);

  const heights = new Float32Array(nx * nz);
  for (let iz = 0; iz < nz; iz++) {
    const z = boundsM.minZ + iz * stepZ;
    for (let ix = 0; ix < nx; ix++) {
      heights[iz * nx + ix] = elevationAt({ x: boundsM.minX + ix * stepX, z });
    }
  }

  return { heights, nx, nz, stepX, stepZ, boundsM };
}

// --- Despike (#629) ----------------------------------------------------------

/**
 * Senkt innere offener-Boden-Grate im Heightfield. Ein Sample gilt als Grat,
 * wenn es auf GEGENÜBERLIEGENDEN Seiten (x- oder z-Paar) von zonengedeckten,
 * TIEFEREN Nachbarn eingeklemmt ist — dann ist es ein sub-grid-schmaler
 * Naht-Streifen zwischen handgezeichneten Zonen, der über tiefen Plateaus als
 * Nadel hochschösse. Er wird auf das HÖHERE der beiden Zonen-Niveaus gezogen
 * (glatte Naht, danach nur die legitime Zonen-Stufe).
 *
 * Unberührt bleiben: legitime Stufenwände (deren hohe Seite ist covered ⇒ das
 * Sample selbst wird übersprungen) und Rasenkanten/-flächen (nur EINE Seite ist
 * Zone ⇒ kein gegenüberliegendes gedecktes Paar). Reine Daten, kein three/React.
 *
 * @param {Float32Array} heights - Original bleibt unberührt (wird kopiert)
 * @param {Uint8Array} inside - 1 = Sample liegt in der Outline
 * @param {Uint8Array} covered - 1 = Sample liegt in einer Ebene/Böschung
 * @returns {Float32Array} geglättete Höhen
 */
export function despikeOpenGround(
  heights,
  inside,
  covered,
  nx,
  nz,
  minDrop = TERRAIN_DESPIKE_MIN_DROP_M,
  passes = TERRAIN_DESPIKE_PASSES
) {
  const out = Float32Array.from(heights);
  const clampPair = (h, aIdx, aOk, bIdx, bOk, src) => {
    if (!aOk || !bOk || !inside[aIdx] || !inside[bIdx]) return null;
    if (!covered[aIdx] || !covered[bIdx]) return null;
    if (src[aIdx] >= h - minDrop || src[bIdx] >= h - minDrop) return null;
    return Math.max(src[aIdx], src[bIdx]); // höhere Zonenseite
  };
  for (let pass = 0; pass < passes; pass++) {
    const src = Float32Array.from(out);
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = 0; ix < nx; ix++) {
        const i = iz * nx + ix;
        if (!inside[i] || covered[i]) continue; // nur innerer offener Boden
        const h = src[i];
        const inX = clampPair(h, i - 1, ix > 0, i + 1, ix < nx - 1, src);
        const inZ = clampPair(h, i - nx, iz > 0, i + nx, iz < nz - 1, src);
        let target = null;
        if (inX !== null) target = inX;
        if (inZ !== null) target = target === null ? inZ : Math.max(target, inZ);
        if (target !== null) out[i] = target;
      }
    }
  }
  return out;
}

// --- Heightfield-Geometrie (Outline-Clipping an der Kante) --------------------

/**
 * Schnittpunkt zweier Strecken p1→p2 und p3→p4 (oder null bei parallel/außerhalb).
 * Gibt zusätzlich den Parameter t auf p1→p2 zurück (0 = p1, 1 = p2).
 */
function segmentSegmentIntersection(p1, p2, p3, p4) {
  const rx = p2.x - p1.x;
  const rz = p2.z - p1.z;
  const sx = p4.x - p3.x;
  const sz = p4.z - p3.z;
  const denom = rx * sz - rz * sx;
  if (Math.abs(denom) < EPS) return null; // parallel oder kollinear
  const qpx = p3.x - p1.x;
  const qpz = p3.z - p1.z;
  const t = (qpx * sz - qpz * sx) / denom;
  const u = (qpx * rz - qpz * rx) / denom;
  if (t < -EPS || t > 1 + EPS || u < -EPS || u > 1 + EPS) return null;
  const tc = Math.min(1, Math.max(0, t));
  return { x: p1.x + tc * rx, z: p1.z + tc * rz, t: tc };
}

/**
 * Erster Outline-Übergang auf der Strecke inside→outside, vom Innenpunkt aus
 * gesehen (Schnittpunkt mit kleinstem t). Der emittierte Rand-Vertex liegt so
 * garantiert auf der Verbindung innen→außen und kann eine konkave Kerbe des
 * Umrisses nicht überqueren — genau das verhinderte der frühere globale
 * closestPointOnPolygon-Snap nicht, wodurch an konkaven Ecken schmale,
 * steile „Zungen" entstanden (#629). Fallback: nächster Outline-Punkt.
 */
function firstBoundaryCrossing(inside, outside, polygon) {
  let best = null;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const hit = segmentSegmentIntersection(inside, outside, polygon[j], polygon[i]);
    if (hit && (best === null || hit.t < best.t)) best = hit;
  }
  if (best) return { x: best.x, z: best.z };
  return closestPointOnPolygon(outside, polygon) || outside;
}

/**
 * Adaptive Zell-Diagonale (#629-Follow-up): true = Diagonale i00–i11, sonst
 * i10–i01. Gewählt wird die Diagonale mit der KLEINEREN Höhendifferenz ihrer
 * Endpunkte — so faltet die Zelle entlang einer diagonalen Knick-Kante
 * (Ebene↔Böschung) statt sie zu „treppen" (Sägezahn). Gleichstand → i00–i11.
 */
export function preferDiagonalA(h00, h10, h01, h11) {
  return Math.abs(h00 - h11) <= Math.abs(h10 - h01);
}

/**
 * Heightfield-FELD: Höhen + Innen-Maske + Despike, einmal pro Build (#684).
 *
 * Bewusst getrennt von der Triangulation, weil dasselbe Feld mehrfach
 * geschnitten wird: einmal an der Garten-Outline (Terrain) und danach an jedem
 * Flächen-Polygon (drapePolygon). Nur wenn alle Schnitte auf demselben Raster
 * mit denselben Höhen sitzen, sind Flächen und Gelände exakt parallel.
 *
 * @param {Function} elevationAt - Sampler aus createElevationSampler
 * @param {object} boundsM - Outline-Bounds in Metern (computeBounds)
 * @param {Array<{x,z}>} outlineWorld - Garten-Outline in Welt-Metern
 * @param {object} [options] - durchgereicht an buildHeightfieldSamples
 * @param {Function} [options.coverAt] - zoneCoverAt aus createElevationSampler;
 *   aktiviert den offener-Boden-Despike (#629)
 * @returns {{ heights: Float32Array, inside: Uint8Array, nx, nz, stepX, stepZ,
 *             boundsM: object, elevationAt: Function }}
 */
export function buildTerrainField(elevationAt, boundsM, outlineWorld, options = {}) {
  const { coverAt = null } = options;
  const samples = buildHeightfieldSamples(elevationAt, boundsM, options);
  const { nx, nz, stepX, stepZ } = samples;
  const clip = Array.isArray(outlineWorld) && outlineWorld.length >= 3;

  const inside = new Uint8Array(nx * nz);
  const covered = coverAt ? new Uint8Array(nx * nz) : null;
  for (let iz = 0; iz < nz; iz++) {
    for (let ix = 0; ix < nx; ix++) {
      const i = iz * nx + ix;
      const p = { x: boundsM.minX + ix * stepX, z: boundsM.minZ + iz * stepZ };
      inside[i] = !clip || insideInclusive(p, outlineWorld) ? 1 : 0;
      if (covered) covered[i] = coverAt(p) ? 1 : 0;
    }
  }

  // Offener-Boden-Naht-Grate glätten (#629), bevor trianguliert wird.
  const heights = covered
    ? despikeOpenGround(samples.heights, inside, covered, nx, nz)
    : Float32Array.from(samples.heights);

  return { heights, inside, nx, nz, stepX, stepZ, boundsM, elevationAt };
}

/**
 * Ein Feld an einem Polygon zuschneiden und triangulieren.
 *
 * Dreiecke komplett außerhalb werden verworfen; randüberlappende Dreiecke werden
 * per Sutherland-Hodgman geclippt: jeder Kantenübergang innen↔außen erzeugt
 * einen Vertex auf dem Schnittpunkt DIESER Kante mit dem Polygon (nicht am
 * global nächsten Polygonpunkt). Dadurch bleibt die Kante glatt statt
 * Raster-Treppe — und an konkaven Ecken entstehen keine über die Kerbe
 * gespannten Zacken (#629).
 *
 * Muss im Adapter laufen (elevationAt verfügbar), nicht im Mesh. Rückgabe ist
 * reine Daten — Terrain-/AreaMesh machen daraus eine BufferGeometry.
 *
 * @param {object} field - aus buildTerrainField
 * @param {object} [options]
 * @param {Uint8Array} [options.mask=field.inside] - 1 = Rasterpunkt gehört zur Region
 * @param {Array<{x,z}>|null} [options.clipPolygon] - Rand für die Schnittpunkte
 * @param {number} [options.yOffset=0] - auf jede Vertex-Höhe addiert
 * @param {Function|null} [options.crossingHeight] - Höhe eines Rand-Vertex
 *   (x, z, iIn, iOut, t) => y; Default = elevationAt am Schnittpunkt
 * @param {object|null} [options.window] - Raster-Index-Fenster {ix0, ix1, iz0, iz1}
 * @returns {{ positions: Float32Array, indices: Uint32Array, minY: number, maxY: number }}
 */
export function triangulateFieldRegion(field, options = {}) {
  const { heights, nx, nz, stepX, stepZ, boundsM } = field;
  const {
    mask = field.inside,
    clipPolygon = null,
    yOffset = 0,
    crossingHeight = null,
    window: win = null,
  } = options;

  const gridX = (ix) => boundsM.minX + ix * stepX;
  const gridZ = (iz) => boundsM.minZ + iz * stepZ;
  const gridPos = (i) => {
    const ix = i % nx;
    return { x: gridX(ix), z: gridZ((i - ix) / nx) };
  };

  const positions = [];
  const indices = [];
  let minY = Infinity;
  let maxY = -Infinity;
  const emit = (x, y, z) => {
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
    const index = positions.length / 3;
    positions.push(x, y, z);
    return index;
  };

  // Inside-Grid-Vertices werden dedupliziert (an festen Rasterpunkten geteilt).
  const remap = new Int32Array(nx * nz).fill(-1);
  const emitInside = (i) => {
    if (remap[i] !== -1) return remap[i];
    const { x, z } = gridPos(i);
    remap[i] = emit(x, heights[i] + yOffset, z);
    return remap[i];
  };

  // Rand-Vertices: pro Vorkommen frisch (Position hängt vom Innen-Partner ab).
  // Terrain-Default: Höhe DIREKT am Schnittpunkt gesampelt ⇒ glatter Rand statt
  // Raster-Treppe (die Rampe läuft stufenlos zum Zaun). Der offener-Boden-Blend
  // in elevationAt verhindert dabei, dass ein Rand-Sample über ein tiefes
  // Plateau schießt (#629). Flächen überschreiben das mit einer Interpolation
  // entlang der Rasterkante (siehe drapePolygon).
  const emitCrossing = (x, z, iIn, iOut, t) => {
    const y = crossingHeight
      ? crossingHeight(x, z, iIn, iOut, t)
      : field.elevationAt({ x, z });
    return emit(x, y + yOffset, z);
  };

  // Ein Rasterdreieck (Grid-Indizes, +y-Winding) an das Polygon clippen.
  const emitTriangle = (t0, t1, t2) => {
    const tri = [t0, t1, t2];
    const insideCount = mask[t0] + mask[t1] + mask[t2];
    if (insideCount === 0) return; // ganz außerhalb
    if (!clipPolygon || insideCount === 3) {
      indices.push(emitInside(t0), emitInside(t1), emitInside(t2));
      return;
    }
    // Sutherland-Hodgman gegen „innerhalb des Polygons"; erhält den Umlaufsinn.
    const ring = [];
    for (let e = 0; e < 3; e++) {
      const cur = tri[e];
      const next = tri[(e + 1) % 3];
      if (mask[cur]) ring.push(emitInside(cur));
      if (mask[cur] !== mask[next]) {
        const iIn = mask[cur] ? cur : next;
        const iOut = mask[cur] ? next : cur;
        const a = gridPos(iIn);
        const b = gridPos(iOut);
        const c = firstBoundaryCrossing(a, b, clipPolygon);
        const span = Math.hypot(b.x - a.x, b.z - a.z);
        const t = span > EPS ? Math.min(1, Math.hypot(c.x - a.x, c.z - a.z) / span) : 0;
        ring.push(emitCrossing(c.x, c.z, iIn, iOut, t));
      }
    }
    // Fan-Triangulation des geclippten Polygons (3 oder 4 Vertices)
    for (let k = 1; k < ring.length - 1; k++) {
      indices.push(ring[0], ring[k], ring[k + 1]);
    }
  };

  const ix0 = win ? Math.max(0, win.ix0) : 0;
  const ix1 = win ? Math.min(nx - 1, win.ix1) : nx - 1;
  const iz0 = win ? Math.max(0, win.iz0) : 0;
  const iz1 = win ? Math.min(nz - 1, win.iz1) : nz - 1;

  for (let iz = iz0; iz < iz1; iz++) {
    for (let ix = ix0; ix < ix1; ix++) {
      const i00 = iz * nx + ix;
      const i10 = i00 + 1;
      const i01 = i00 + nx;
      const i11 = i01 + 1;
      // Diagonale pro Zelle adaptiv wählen (#629-Follow-up gegen Sägezahn an
      // diagonalen Knick-Kanten Ebene↔Böschung): entlang der Diagonale mit der
      // kleineren Höhendifferenz teilen, damit die Faltung der Knick-Kante folgt
      // statt sie zu „treppen". Beide Windings zeigen nach +y (Höhe +y).
      // Hängt NUR an den Feld-Höhen ⇒ eine drapierte Fläche wählt dieselbe
      // Diagonale wie das Terrain darunter und bleibt exakt parallel (#684).
      if (preferDiagonalA(heights[i00], heights[i10], heights[i01], heights[i11])) {
        emitTriangle(i00, i01, i11);
        emitTriangle(i00, i11, i10);
      } else {
        emitTriangle(i00, i01, i10);
        emitTriangle(i10, i01, i11);
      }
    }
  }

  if (positions.length === 0) {
    minY = 0;
    maxY = 0;
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    minY,
    maxY,
  };
}

/**
 * Heightfield als indexierte Dreiecks-Geometrie, auf die Garten-Outline
 * zugeschnitten (Feld + Triangulation in einem Aufruf).
 *
 * @param {Function} elevationAt - Sampler aus createElevationSampler
 * @param {object} boundsM - Outline-Bounds in Metern (computeBounds)
 * @param {Array<{x,z}>} outlineWorld - Garten-Outline in Welt-Metern
 * @param {object} [options] - durchgereicht an buildTerrainField
 * @param {object} [options.field] - bereits gebautes Feld (spart das Re-Sampling,
 *   wenn der Aufrufer es ohnehin fürs Drapieren braucht)
 * @returns {{ positions: Float32Array, indices: Uint32Array,
 *             minY: number, maxY: number, nx: number, nz: number, boundsM: object }}
 */
export function buildTerrainGeometry(elevationAt, boundsM, outlineWorld, options = {}) {
  const field = options.field ?? buildTerrainField(elevationAt, boundsM, outlineWorld, options);
  const clip = Array.isArray(outlineWorld) && outlineWorld.length >= 3;
  const geometry = triangulateFieldRegion(field, {
    clipPolygon: clip ? outlineWorld : null,
  });
  return { ...geometry, nx: field.nx, nz: field.nz, boundsM: field.boundsM };
}

/**
 * Ein Flächen-Polygon über das Heightfield DRAPIEREN (#684).
 *
 * Die Fläche wird aus demselben Raster geschnitten wie das Terrain — gleiche
 * Rasterpunkte, gleiche (despikte) Höhen, gleiche adaptive Zell-Diagonale.
 * Damit ist jedes Flächendreieck exakt parallel zum Geländedreieck darunter und
 * der senkrechte Abstand überall konstant `yOffset`. Daraus folgt: kein
 * Durchstoßen an Böschungskanten, kein Z-Fighting, und zwei gestapelte Flächen
 * (Weg auf Rasen) behalten ihre Reihenfolge über die volle Länge.
 *
 * Rand-Vertices interpolieren LINEAR entlang der Rasterkante statt elevationAt
 * neu zu sampeln: nur so liegt auch der Rand exakt auf der Geländefläche (ein
 * frisches Sample läge auf der gekrümmten Höhenfunktion und könnte an steilen
 * Kanten unter das Dreieck tauchen).
 *
 * Grenze: Ein Polygon, das schmaler ist als eine Rasterzelle, kann Zellen
 * überlappen, in denen KEIN Rasterpunkt innen liegt — dort entstünde eine Lücke.
 * Für gerade Ränder ist das ausgeschlossen (eine Halbebene, die ein Dreieck
 * schneidet, enthält immer einen Eckpunkt); relevant wäre nur ein Band schmaler
 * als ~2 Zelldiagonalen. Die schmalste Fläche ist ein Weg mit 0,40 m Mindest-
 * breite gegen 0,125 m Raster — mit Reserve außerhalb des Problemfalls.
 *
 * Ragt eine Fläche über den Rasterrand (= Bbox der Garten-Outline) hinaus, wird
 * der überstehende Teil abgeschnitten. Das ist gewollt: dort endet auch das
 * Terrain, die Fläche würde sonst über dem Nichts schweben. Liegt sie KOMPLETT
 * außerhalb, kommt null zurück und der Aufrufer bleibt beim Anker.
 *
 * @param {object} field - aus buildTerrainField
 * @param {Array<{x,z}>} polygon - Flächen-Polygon in Welt-Metern
 * @param {number} [yOffset=0] - Z-Fighting-Sprosse, in die Vertices eingebacken
 * @returns {{ positions: Float32Array, indices: Uint32Array,
 *             minY: number, maxY: number }|null} null = nicht drapierbar
 */
export function drapePolygon(field, polygon, yOffset = 0) {
  if (!Array.isArray(polygon) || polygon.length < 3) return null;
  const { heights, nx, nz, stepX, stepZ, boundsM } = field;
  if (!(stepX > EPS) || !(stepZ > EPS)) return null;

  // Index-Fenster der Polygon-Bbox, je eine Zelle Rand: Randdreiecke müssen
  // vollständig mitlaufen, damit das Clipping sie erwischt.
  const b = polygonBounds(polygon);
  const ix0 = Math.max(0, Math.floor((b.minX - boundsM.minX) / stepX) - 1);
  const ix1 = Math.min(nx - 1, Math.ceil((b.maxX - boundsM.minX) / stepX) + 1);
  const iz0 = Math.max(0, Math.floor((b.minZ - boundsM.minZ) / stepZ) - 1);
  const iz1 = Math.min(nz - 1, Math.ceil((b.maxZ - boundsM.minZ) / stepZ) + 1);
  if (ix1 <= ix0 || iz1 <= iz0) return null; // Fläche liegt außerhalb des Rasters

  // Innen-Test ohne Randtoleranz: die 1-mm-Inklusion aus insideInclusive kostet
  // pro AUSSEN-Punkt eine Segment-Schleife — bei mehreren Flächen der teuerste
  // Posten im ganzen Build, und sub-mm entscheidet an einer Flächenkante nichts.
  const mask = new Uint8Array(nx * nz);
  for (let iz = iz0; iz <= iz1; iz++) {
    const z = boundsM.minZ + iz * stepZ;
    for (let ix = ix0; ix <= ix1; ix++) {
      const x = boundsM.minX + ix * stepX;
      if (x < b.minX || x > b.maxX || z < b.minZ || z > b.maxZ) continue;
      if (pointInPolygon({ x, z }, polygon)) mask[iz * nx + ix] = 1;
    }
  }

  const geometry = triangulateFieldRegion(field, {
    mask,
    clipPolygon: polygon,
    yOffset,
    crossingHeight: (x, z, iIn, iOut, t) => heights[iIn] + (heights[iOut] - heights[iIn]) * t,
    window: { ix0, ix1, iz0, iz1 },
  });
  return geometry.indices.length > 0 ? geometry : null;
}
